import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { Select } from '@/components/Select'
import { RaspadinhaWinnersTable } from '@/components/RaspadinhaWinnersTable'
import { WinnersSectionCard } from '@/components/WinnersSectionCard'
import { useInstitutionScratchCampaignSelect } from '@/hooks/useInstitutionScratchCampaignSelect'
import { getScratchCampaign } from '@/lib/api/scratch-campaigns.service'
import type { RaspadinhaCampaign } from '@/types/raspadinha-campaign'
import sectionStyles from './WinnersPageSections.module.css'
import styles from './RaspadinhaWinnersSection.module.css'

export function RaspadinhaWinnersSection() {
  const {
    campaignOptions,
    campaignValue,
    handleCampaignValueChange,
    selectPlaceholder,
    campaignsLoading,
    campaignsError,
    isSelectorDisabled,
    isAdminWithoutInstitution,
    scratchCampaignId,
  } = useInstitutionScratchCampaignSelect()

  const [loadedCampaign, setLoadedCampaign] = useState<RaspadinhaCampaign | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<{ campaignId: number; message: string } | null>(
    null
  )
  const fetchGenerationRef = useRef(0)

  useEffect(() => {
    if (isAdminWithoutInstitution || scratchCampaignId == null) {
      return
    }

    const generation = ++fetchGenerationRef.current
    queueMicrotask(() => {
      if (generation !== fetchGenerationRef.current) {
        return
      }
      setDetailLoading(true)
      setDetailError(null)
    })

    void getScratchCampaign(scratchCampaignId)
      .then((campaign) => {
        if (generation !== fetchGenerationRef.current) {
          return
        }
        setLoadedCampaign(campaign)
      })
      .catch(() => {
        if (generation !== fetchGenerationRef.current) {
          return
        }
        setDetailError({
          campaignId: scratchCampaignId,
          message: 'Não foi possível carregar os ganhadores da campanha raspadinha.',
        })
      })
      .finally(() => {
        if (generation === fetchGenerationRef.current) {
          setDetailLoading(false)
        }
      })
  }, [isAdminWithoutInstitution, scratchCampaignId])

  const campaignDetail =
    loadedCampaign && loadedCampaign.id === scratchCampaignId ? loadedCampaign : null

  const activeDetailError =
    detailError && detailError.campaignId === scratchCampaignId ? detailError.message : null

  const isDetailLoading =
    !isAdminWithoutInstitution &&
    scratchCampaignId != null &&
    (detailLoading || (campaignDetail == null && activeDetailError == null))

  const campaignSelector = (
    <div className={styles.selectorWrap}>
      <Select
        id="winner-lookup-raspadinha-select"
        value={campaignValue}
        onChange={handleCampaignValueChange}
        options={campaignOptions}
        placeholder={selectPlaceholder}
        label="Campanha raspadinha"
        icon="mdi:ticket-percent-outline"
        disabled={isSelectorDisabled}
      />
      {campaignsError ? (
        <p className={styles.selectorError} role="alert">
          {campaignsError}
        </p>
      ) : null}
    </div>
  )

  return (
    <WinnersSectionCard
      id="winners-raspadinha-section"
      title="Ganhadores da raspadinha"
      subtitle="Lista de contemplados por dia e horário na campanha raspadinha selecionada."
      icon="mdi:ticket-outline"
      headerAction={campaignSelector}
    >
      {isAdminWithoutInstitution ? (
        <p className={sectionStyles.emptyMessage}>
          Selecione uma instituição no topo para consultar ganhadores da raspadinha.
        </p>
      ) : campaignsLoading ? (
        <div className={sectionStyles.centered}>
          <Icon icon="mdi:loading" className={sectionStyles.spinner} />
          Carregando campanhas raspadinha...
        </div>
      ) : campaignOptions.length === 0 ? (
        <p className={sectionStyles.emptyMessage}>
          Nenhuma campanha raspadinha cadastrada. Crie uma campanha raspadinha para listar
          ganhadores.
        </p>
      ) : isDetailLoading ? (
        <div className={sectionStyles.centered}>
          <Icon icon="mdi:loading" className={sectionStyles.spinner} />
          Carregando ganhadores...
        </div>
      ) : activeDetailError ? (
        <p className={sectionStyles.errorMessage} role="alert">
          {activeDetailError}
        </p>
      ) : campaignDetail ? (
        <RaspadinhaWinnersTable
          winning_date_schedules={campaignDetail.winning_date_schedules}
          participants={campaignDetail.participants}
          slug={campaignDetail.slug}
        />
      ) : (
        <p className={sectionStyles.emptyMessage}>
          Selecione uma campanha raspadinha para visualizar os ganhadores.
        </p>
      )}
    </WinnersSectionCard>
  )
}
